import React, { useEffect, useState, createContext, useContext } from 'react';
import { User } from '../types/user';
import { users as initialUsers } from '../data/users';
import { loadFromDatabase, saveToDatabase } from '../utils/database';
interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => boolean;
  logout: () => void;
  canAddVehicle: (stationId: string) => boolean;
  canRemoveVehicle: (stationId: string) => boolean;
  canUpdateStatus: (stationId: string) => boolean;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export function AuthProvider({
  children
}: {
  children: ReactNode;
}) {
  const [user, setUser] = useState<User | null>(null);
  const [availableUsers, setAvailableUsers] = useState<User[]>([]);
  // Charger les utilisateurs depuis la base de données au démarrage
  useEffect(() => {
    const savedData = loadFromDatabase();
    if (savedData.users && savedData.users.length > 0) {
      // Utiliser les utilisateurs sauvegardés
      setAvailableUsers(savedData.users);
    } else {
      // Initialiser avec les utilisateurs par défaut
      setAvailableUsers(initialUsers);
      saveToDatabase({
        users: initialUsers
      });
    }
  }, []);
  const login = (username: string, password: string): boolean => {
    // Charger les utilisateurs les plus récents depuis la base de données
    const savedData = loadFromDatabase();
    const usersToCheck = savedData.users && savedData.users.length > 0 ? savedData.users : availableUsers;
    const foundUser = usersToCheck.find(u => u.username === username && u.password === password);
    if (foundUser) {
      setUser(foundUser);
      return true;
    }
    return false;
  };
  const logout = () => {
    setUser(null);
  };
  const canAddVehicle = (stationId: string): boolean => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    // Superviseur général ne peut pas ajouter de véhicules
    if (user.role === 'general-supervisor') return false;
    if (user.role === 'supervisor' || user.role === 'operator') {
      return user.assignedStationId === stationId;
    }
    return false;
  };
  const canRemoveVehicle = (stationId: string): boolean => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    // Superviseur général ne peut pas supprimer de véhicules
    if (user.role === 'general-supervisor') return false;
    if (user.role === 'supervisor') {
      return user.assignedStationId === stationId;
    }
    return false;
  };
  const canUpdateStatus = (stationId: string): boolean => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    // Superviseur général ne peut pas modifier les statuts
    if (user.role === 'general-supervisor') return false;
    if (user.role === 'supervisor') {
      return user.assignedStationId === stationId;
    }
    return false;
  };
  return <AuthContext.Provider value={{
    user,
    login,
    logout,
    canAddVehicle,
    canRemoveVehicle,
    canUpdateStatus
  }} data-id="element-1132">
      {children}
    </AuthContext.Provider>;
}
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}